import { useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, CircleDot, CheckCircle2, AlertCircle } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { trackEvent } from '../utils/tracking';
import { verticalPathFor } from '../utils/verticalPaths';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

// Helper function to normalize image URLs
const normalizeImageUrl = (url) => {
  if (!url) return null;
  if (typeof url === 'object' && url.url) {
    return url.url;
  }
  if (typeof url !== 'string') return null;
  if (url.startsWith('https://images.unsplash.com')) return url;
  if (url.startsWith('http')) return url;
  if (url.startsWith('/api/')) {
    return `${API_BASE_URL}${url}`;
  }
  if (url.includes('/api/uploads/')) {
    const match = url.match(/\/api\/uploads\/.+$/);
    if (match) {
      return `${API_BASE_URL}${match[0]}`;
    }
  }
  return url;
};

const CATEGORY_IMAGES = {
  electronics: 'https://images.unsplash.com/photo-1750797308931-b0d261abb3d5?w=400&h=300&fit=crop',
  furniture: 'https://images.unsplash.com/photo-1673695008224-fa5a9d195dfb?w=400&h=300&fit=crop',
  camera: 'https://images.unsplash.com/photo-1629843041291-d374ccae235a?w=400&h=300&fit=crop',
  clothing: 'https://images.unsplash.com/photo-1489987707025-afc232f7ea0f?w=400&h=300&fit=crop',
  vehicles: 'https://images.unsplash.com/photo-1549317661-bd32c8ce0db2?w=400&h=300&fit=crop',
  sports: 'https://images.unsplash.com/photo-1461896836934-480b7090c4c7?w=400&h=300&fit=crop',
  home: 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=400&h=300&fit=crop',
  collectibles: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&h=300&fit=crop',
  books: 'https://images.unsplash.com/photo-1512820790803-83ca734da794?w=400&h=300&fit=crop',
  tools: 'https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=400&h=300&fit=crop',
  other: 'https://images.unsplash.com/photo-1586864387967-d02ef85d93e8?w=400&h=300&fit=crop',
};

// Condition icon helper
const getConditionIcon = (condition) => {
  const c = condition?.toLowerCase()?.replace('_', ' ');
  if (c === 'new' || c === 'like new') {
    return <CheckCircle2 className="w-3.5 h-3.5 text-[#059669]" title={c} />;
  }
  if (c === 'good' || c === 'excellent') {
    return <CircleDot className="w-3.5 h-3.5 text-[#C9956C]" title={c} />;
  }
  return <AlertCircle className="w-3.5 h-3.5 text-[var(--theme-text-muted)]" title={c || 'Unknown'} />;
};

export const CatalogCard = ({ item, compact = false, linkPrefix }) => {
  const normalizedItemImage = normalizeImageUrl(item.images?.[0]);
  const imageUrl = normalizedItemImage || CATEGORY_IMAGES[item.category] || CATEGORY_IMAGES.other;
  const price = item.price != null ? item.price : item.estimated_value;
  const isFree = item.is_free || price === 0;
  const isEoi = !!item.is_eoi;
  const isPlus = item.category === 'vehicles'
    && item.vehicle_details?.listing_tier === 'plus';

  const resolvedPrefix = linkPrefix || verticalPathFor(item);
  const href = `${resolvedPrefix === '/catalog' ? '/catalog' : resolvedPrefix}/${item.id}`;

  // Track if image has extreme aspect ratio
  const [isExtremeAspect, setIsExtremeAspect] = useState(false);

  // Detect aspect ratio on image load
  const handleImageLoad = (e) => {
    const img = e.target;
    const aspectRatio = img.naturalWidth / img.naturalHeight;
    // Extreme if very wide (>2:1) or very tall (<1:2)
    setIsExtremeAspect(aspectRatio > 2 || aspectRatio < 0.5);
  };

  const handleClick = () => {
    trackEvent('click', { item_id: item.id, category: item.category, subcategory: item.subcategory });
  };

  return (
    <Link to={href} data-testid={`catalog-card-${item.id}`} className="block" onClick={handleClick}>
      <div className={`bg-[var(--theme-card)] rounded-xl overflow-hidden border transition-all duration-300 group ${
        isPlus
          ? 'border-[#C9956C]/40 ring-1 ring-[#C9956C]/20 hover:border-[#C9956C]/60'
          : 'border-[var(--theme-border)] hover:border-[var(--theme-border-hover)]'
      }`}>
        
        {/* Image - Adaptive aspect ratio handling */}
        <div className="relative aspect-square overflow-hidden bg-[var(--theme-hover)]">
          {/* Blur background for extreme aspect ratios */}
          {isExtremeAspect && (
            <div 
              className="absolute inset-0 bg-cover bg-center blur-xl scale-110 opacity-40"
              style={{ backgroundImage: `url(${imageUrl})` }}
            />
          )}
          
          <img 
            src={imageUrl} 
            alt={item.title}
            className={`w-full h-full transition-transform duration-500 group-hover:scale-105 relative z-10 ${
              isExtremeAspect ? 'object-contain' : 'object-cover'
            }`}
            onLoad={handleImageLoad}
            onError={(e) => {
              if (!e.target.dataset.fallback) {
                e.target.dataset.fallback = '1';
                e.target.src = CATEGORY_IMAGES[item.category] || CATEGORY_IMAGES.other;
                setIsExtremeAspect(false);
              } else {
                // Fallback also failed — use a transparent pixel to stop the loop
                e.target.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
              }
            }}
          />
          {isPlus && (
            <div className="absolute top-2 left-2 z-10 px-1.5 py-0.5 rounded bg-[#C9956C]/90 text-white text-[9px] font-bold uppercase tracking-wider">
              Plus
            </div>
          )}
        </div>

        {/* Content - Clean and minimal */}
        <div className="p-2.5">
          {/* Title - Allow 2 lines */}
          <h3 className="font-semibold text-sm text-[var(--theme-text-primary)] line-clamp-2 max-h-[2.2rem] overflow-hidden group-hover:text-[#1E3A5F] transition-colors leading-tight">
            {item.title}
          </h3>
          
          {/* Price */}
          <div className="text-base font-bold text-[#059669] mt-0.5">
            {isEoi ? (
              <span className="inline-flex items-center gap-1">
                <span className="bg-[#1E3A5F] text-white text-[10px] font-bold px-1.5 py-0.5 rounded">EOI</span>
              </span>
            ) : isFree ? (
              <span className="inline-flex items-center gap-1">
                <span className="bg-[#059669] text-white text-[10px] font-bold px-1.5 py-0.5 rounded">FREE</span>
              </span>
            ) : (
              <>
                ${price?.toLocaleString()}
                {item.is_firm && <span className="text-[10px] font-medium text-[var(--theme-text-muted)] ml-1">FIRM</span>}
              </>
            )}
          </div>

          {/* Listed timestamp */}
          {item.created_at && (
            <div className="text-[10px] text-[#6B7280] mt-0.5">
              Listed {formatDistanceToNow(new Date(item.created_at), { addSuffix: true })}
            </div>
          )}

          {/* Footer row: Location + Condition + Distance */}
          <div className="flex items-center justify-between mt-2 text-[11px] text-[var(--theme-text-muted)]">
            <div className="flex items-center gap-1.5 flex-1 min-w-0">
              <MapPin className="w-3 h-3 flex-shrink-0 text-[var(--theme-text-muted)]" />
              <span className="truncate">{item.location_name || item.location?.name || 'Unknown'}</span>
            </div>
            
            <div className="flex items-center gap-2 flex-shrink-0 ml-2">
              {/* Condition icon */}
              {getConditionIcon(item.condition)}
              
              {/* Distance */}
              {item.distance_km != null && (
                <span className="text-[#1E3A5F] font-medium">
                  {item.distance_km < 1 ? '<1' : item.distance_km.toFixed(0)} km
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
};
